// WB-M1B S2 (doc 23 §16.3, decision O8) -- the pure geometry and scale rules
// of a board equation. No Excalidraw, no renderer, no DOM: numbers in,
// numbers out.
//
// An equation at rest is an ordinary text element (the carrier); at runtime
// it is an image element of a different size. Neither box is authoritative
// for position: the element's ROTATED TOP-LEFT CORNER in world space (its
// anchor) is. Excalidraw rotates an element about its centre, so for a box
// (x, y, w, h, θ):
//   centre  C = (x + w/2, y + h/2)
//   anchor  A = C + R(θ)·(−w/2, −h/2)
// and a box of another size (w', h') with the same anchor and angle has
//   C' = A + R(θ)·(w'/2, h'/2),  x' = C'.x − w'/2,  y' = C'.y − h'/2.
// R(θ) is Excalidraw's own rotation (y grows downwards on the canvas):
//   R(θ)·(u, v) = (u cos θ − v sin θ, u sin θ + v cos θ).
// Frozen WB-M1A keeps exactly this corner fixed when it re-measures a
// left-aligned text element (doc 23 §16.7), which is why a carrier can be
// re-measured, moved or edited there and still come back in place.
//
// Scale is `customData.borna.em`: scene pixels per em of the renderer's
// picture. Never derived from the carrier's width / height.

import { BOARD_EQUATION_LIMITS } from './boardEquation'

// The `em` range (S2), derived from the two limits it has to respect:
//
//   minimum 1   Excalidraw 0.17.6's MIN_FONT_SIZE: the smallest font size it
//               lets a text element have. `em` is the equation's font size
//               (MathJax's em is the font's em), so an equation can be as
//               small as board text can, no smaller.
//   maximum     Excalidraw 0.17.6 draws an element through its own canvas and
//               lowers that canvas's resolution once a side would exceed
//               WIDTH_HEIGHT_LIMIT = 32767 px (cappedElementCanvasSize). The
//               widest picture the renderer allows is
//               BOARD_EQUATION_LIMITS.maxNaturalWidthEm (400 em), so
//               32767 / 400 = 81.9175 is the largest `em` at which every
//               allowed equation still draws at full resolution at zoom 1.
//               At that `em` the largest picture is 32767 x 16383.5 scene px,
//               far inside Subway's coordinate bound (1e7).
export const EXCALIDRAW_MIN_FONT_SIZE = 1
export const EXCALIDRAW_CANVAS_SIDE_LIMIT = 32767
export const BOARD_EQUATION_EM_MIN = EXCALIDRAW_MIN_FONT_SIZE
export const BOARD_EQUATION_EM_MAX =
  EXCALIDRAW_CANVAS_SIDE_LIMIT / BOARD_EQUATION_LIMITS.maxNaturalWidthEm

/**
 * Whether a value is a usable `borna.em`: a finite number inside the range.
 * @param {*} em
 */
export function isValidEquationEm(em) {
  return (
    typeof em === 'number' &&
    Number.isFinite(em) &&
    em >= BOARD_EQUATION_EM_MIN &&
    em <= BOARD_EQUATION_EM_MAX
  )
}

/**
 * The `em` a proportional resize means (doc 23 §16.3, D.6): the new height
 * over the picture's natural height in em, kept inside the range. For S3;
 * S2 does not resize anything.
 * @param {number} runtimeHeight -- the image's height in scene px
 * @param {number} naturalHeightEm -- the renderer's natural height
 * @returns {number|null} null when either input is not a positive finite
 *   number
 */
export function deriveEquationEm(runtimeHeight, naturalHeightEm) {
  if (
    typeof runtimeHeight !== 'number' ||
    typeof naturalHeightEm !== 'number' ||
    !Number.isFinite(runtimeHeight) ||
    !Number.isFinite(naturalHeightEm) ||
    runtimeHeight <= 0 ||
    naturalHeightEm <= 0
  ) {
    return null
  }
  const em = runtimeHeight / naturalHeightEm
  if (!Number.isFinite(em)) return null
  return Math.min(BOARD_EQUATION_EM_MAX, Math.max(BOARD_EQUATION_EM_MIN, em))
}

/**
 * The rotated top-left corner of a box, in world space.
 * @param {{x: number, y: number, width: number, height: number,
 *   angle: number}} box
 * @returns {{x: number, y: number}}
 */
export function rotatedTopLeft(box) {
  const { x, y, width, height, angle } = box
  if (angle === 0) return { x, y }
  const cos = Math.cos(angle)
  const sin = Math.sin(angle)
  const u = -width / 2
  const v = -height / 2
  return {
    x: x + width / 2 + (u * cos - v * sin),
    y: y + height / 2 + (u * sin + v * cos),
  }
}

/**
 * The (x, y) of a box of the given size and angle whose rotated top-left
 * corner is `anchor`.
 * @param {{x: number, y: number}} anchor
 * @param {number} width
 * @param {number} height
 * @param {number} angle
 * @returns {{x: number, y: number}}
 */
export function boxAtAnchor(anchor, width, height, angle) {
  if (angle === 0) return { x: anchor.x, y: anchor.y }
  const cos = Math.cos(angle)
  const sin = Math.sin(angle)
  const u = width / 2
  const v = height / 2
  return {
    x: anchor.x + (u * cos - v * sin) - width / 2,
    y: anchor.y + (u * sin + v * cos) - height / 2,
  }
}

/**
 * Where a box of a new size goes so that it keeps `from`'s rotated top-left
 * corner and angle. Exact (no arithmetic at all) when the angle is 0 or the
 * size does not change, so a carrier that is loaded and saved unchanged
 * keeps its numbers bit for bit.
 * @param {{x: number, y: number, width: number, height: number,
 *   angle: number}} from
 * @param {number} width
 * @param {number} height
 * @returns {{x: number, y: number}}
 */
export function resizeAtAnchor(from, width, height) {
  if (from.angle === 0 || (width === from.width && height === from.height)) {
    return { x: from.x, y: from.y }
  }
  return boxAtAnchor(rotatedTopLeft(from), width, height, from.angle)
}

// The `em` a new equation is inserted at (S3). Board text is written at
// Excalidraw's default 20 px in the Helvetica alias, whose x-height is about
// 0.52 em = 10.5 px; MathJax's TeX italic has an x-height of 0.43 em, so at
// 24 px per em (10.3 px) a formula's letters are the size of the board text
// next to it.
export const BOARD_EQUATION_DEFAULT_EM = 24

// How far a runtime height may be from `natural height x em` and still be
// "the height `em` gives": far above the rounding of one multiplication, far
// below anything a pointer can do.
const EM_HEIGHT_TOLERANCE = 1e-9

/**
 * The `em` of a runtime equation image of the given height (S3, doc 23
 * §16.3 D.6). While the height is still the one the stored `em` gives, the
 * stored `em` is returned EXACTLY, so a save without a resize never perturbs
 * it; after a resize it is the height over the natural height, inside the
 * range. Only the height counts: a non-proportional stretch is not
 * authoritative, the picture's own aspect ratio comes back at this `em`
 * (`equationBoxAtEm`).
 * @param {number} storedEm -- `customData.borna.em`
 * @param {number} runtimeHeight -- the image's height in scene px
 * @param {number} naturalHeightEm -- the renderer's natural height
 * @returns {number} `storedEm` when nothing can be derived
 */
export function equationEmForHeight(storedEm, runtimeHeight, naturalHeightEm) {
  const derived = deriveEquationEm(runtimeHeight, naturalHeightEm)
  if (derived === null) return storedEm
  if (isValidEquationEm(storedEm)) {
    const expected = naturalHeightEm * storedEm
    if (
      Math.abs(runtimeHeight - expected) <=
      EM_HEIGHT_TOLERANCE * Math.max(1, expected)
    ) {
      return storedEm
    }
  }
  return derived
}

/**
 * The box a runtime equation image must have for its geometry to be
 * persisted exactly (doc 23 §21.2): the picture's natural size at `em`, at
 * the image's own rotated top-left anchor and angle -- the box a reload
 * reconstructs from its carrier. Null when the image already has it (within
 * the rounding of one multiplication): a proportional resize inside the
 * `em` range is never touched. Not null after a non-proportional stretch,
 * or when the height asks for an `em` outside the range.
 * @param {{x: number, y: number, width: number, height: number,
 *   angle: number}} box -- the image as it is
 * @param {{widthEm: number, heightEm: number}} natural
 * @param {number} em -- the authoritative scale (`equationEmForHeight`)
 * @returns {{x: number, y: number, width: number, height: number}|null}
 */
export function equationBoxAtEm(box, natural, em) {
  const width = natural.widthEm * em
  const height = natural.heightEm * em
  const same = (actual, expected) =>
    Math.abs(actual - expected) <= EM_HEIGHT_TOLERANCE * Math.max(1, expected)
  if (same(box.width, width) && same(box.height, height)) return null
  const { x, y } = resizeAtAnchor(box, width, height)
  return { x, y, width, height }
}

/**
 * Whether a scene point is inside a (rotated) element box.
 * @param {{x: number, y: number}} point
 * @param {{x: number, y: number, width: number, height: number,
 *   angle: number}} box
 */
export function isPointInBox(point, box) {
  const cx = box.x + box.width / 2
  const cy = box.y + box.height / 2
  // Into the box's own frame: rotate the point back about the centre.
  const cos = Math.cos(-box.angle)
  const sin = Math.sin(-box.angle)
  const dx = point.x - cx
  const dy = point.y - cy
  const u = dx * cos - dy * sin
  const v = dx * sin + dy * cos
  return Math.abs(u) <= box.width / 2 && Math.abs(v) <= box.height / 2
}
